import { createReference, MedplumClient, parseReference, resolveId } from "@medplum/core";
import {
  Bundle,
  Encounter,
  EncounterParticipant,
  Observation,
  Practitioner,
  QuestionnaireResponse,
  Reference,
} from "@medplum/fhirtypes";

import * as Sentry from "@sentry/react";
import pick from "lodash/pick";

import {
  ClientPostpartumFlowsheetForms,
  NewbornPostpartumFlowsheetForms,
  PostpartumClientQuestionnaireType,
  PostpartumNewbornQuestionnaireType,
  PostpartumSOSQuestionnaireType,
} from "@/encounters/components/EncounterLabor/constants/postpartum-flowsheets";
import {
  LaborStage,
  LaborStages,
  StageRow,
} from "@/encounters/components/EncounterLabor/types/labor";
import {
  getLaborStageObservation,
  isBirthingObservation,
} from "@/encounters/components/EncounterLabor/utils/labor";
import {
  collectLaborStageMemberSeedIdsFromReferences,
  expandIncludedLaborFlowsheetObservationIdsUntilStable,
  fetchQuestionnaireResponseIdsLinkedToLaborStage,
} from "@/encounters/components/EncounterLabor/utils/labor-stage-version-diff";
import { OTHERS_PRESENT_UNKNOWN_RELATIONSHIP } from "@/encounters/constants/others-present-relationship";
import { assertLaborAmendMembershipWriteAllowed } from "@/encounters/utils/labor-amend-membership-write-guard";
import { isDoulaOrOthersPresent } from "@/encounters/utils/others-present-participants";
import { applyQuestionnaireResponseEncounterParentVersionId } from "@/encounters/utils/questionnaire-response-amend-stamp";
import { BeforeChartingFormValues } from "@/encounters/zod-schemas/labor";
import { Extensions } from "@/fhir/enums/extensions";
import { LOINCAnswerCodes, LOINCCodes } from "@/fhir/enums/loinc";
import {
  BirthingComponent,
  EncounterPractitionerType,
  EncounterPractitionerTypeDisplay,
  LaborFlowsheetForms,
  QuiltedHealthCodes,
  QuiltedHealthCodeSystems,
} from "@/fhir/enums/quilted-health";
import { Systems } from "@/fhir/enums/systems";
import { setAccountsBestEffort } from "@/utils/accounts";
import { getLocalDateString, localTimeFromISO } from "@/utils/datetime";
import {
  getQuestionnaireResponseIdFromObservationDerivedFrom,
  getResourcesFromBundle,
  joinReferenceString,
  joinTokenString,
  setExtensionValue,
} from "@/utils/medplum";
import { showSystemErrorNotification } from "@/utils/notifications";
import {
  getQuestionnaireResponseFromFormValues,
  QuestionnaireFormValues,
} from "@/utils/questionnaires";

export const createLaborStageObservation = async (
  medplum: MedplumClient,
  clientId: string,
  encounterId: string,
  practitioner: Practitioner,
  stage: LaborStage,
): Promise<Observation> => {
  const encounterReference = joinReferenceString("Encounter", encounterId);
  const patientReference = joinReferenceString("Patient", clientId);

  const observation = getLaborStageObservation(
    "final",
    { reference: patientReference },
    { reference: encounterReference },
    createReference(practitioner),
    new Date(),
    stage,
  );

  return await medplum.createResource(observation);
};

const MEDPLUM_CACHE_RELOAD_OPTIONS = { cache: "reload" } as const;

export async function fetchLaborStageObservations(
  medplum: MedplumClient,
  clientId: string,
  encounterId: string,
) {
  const patientReference = joinReferenceString("Patient", clientId);
  const encounterReference = joinReferenceString("Encounter", encounterId);
  const observations = await medplum.searchResources(
    "Observation",
    {
      subject: patientReference,
      encounter: encounterReference,
      code: joinTokenString(Systems.LOINC, LOINCCodes.LaborStage),
      "status:not": "cancelled",
    },
    MEDPLUM_CACHE_RELOAD_OPTIONS,
  );

  // Keep ordering deterministic so Redux entity replacement by stage code
  // consistently leaves the newest observation version as the final value.
  // Medplum versionId is not numeric (often UUID-like), so order by
  // lastUpdated first and only use version/id as deterministic tie-breakers.
  return [...observations].sort((firstObservation, secondObservation) => {
    const firstLastUpdatedTime = new Date(firstObservation.meta?.lastUpdated ?? 0).getTime() || 0;
    const secondLastUpdatedTime = new Date(secondObservation.meta?.lastUpdated ?? 0).getTime() || 0;
    if (firstLastUpdatedTime !== secondLastUpdatedTime) {
      return firstLastUpdatedTime - secondLastUpdatedTime;
    }

    const versionCompare = (firstObservation.meta?.versionId ?? "").localeCompare(
      secondObservation.meta?.versionId ?? "",
    );
    if (versionCompare !== 0) {
      return versionCompare;
    }

    return (firstObservation.id ?? "").localeCompare(secondObservation.id ?? "");
  });
}

/**
 * Fetches labor stage observations and all their member observations for version diff
 */
export async function fetchLaborStageObservationsWithMembers(
  medplum: MedplumClient,
  clientId: string,
  encounterId: string,
) {
  const patientReference = joinReferenceString("Patient", clientId);
  const encounterReference = joinReferenceString("Encounter", encounterId);

  // First, fetch labor stage observations
  const laborStageObservations = await medplum.searchResources("Observation", {
    subject: patientReference,
    encounter: encounterReference,
    code: joinTokenString(Systems.LOINC, LOINCCodes.LaborStage),
    "status:not": "cancelled",
  });

  // Collect all member observation IDs
  const memberIds: string[] = [];
  laborStageObservations.forEach((stage) => {
    if (stage.hasMember) {
      stage.hasMember.forEach((member) => {
        const id = parseReference(member)[1];
        if (id) memberIds.push(id);
      });
    }
  });

  // Fetch all member observations
  let memberObservations: Observation[] = [];
  if (memberIds.length > 0) {
    const memberResponse = await medplum.searchResources("Observation", {
      _id: memberIds.join(","),
      subject: patientReference,
      encounter: encounterReference,
      _sort: "-_lastUpdated",
    });
    memberObservations = memberResponse;
  }

  // Return both labor stage observations and member observations
  return [...laborStageObservations, ...memberObservations];
}

/**
 * Expands {@link fetchLaborStageObservationsWithMembers} with amend successor observations.
 *
 * Stage `hasMember` often still references the pre-amend observation id after an edit; bots create
 * a new Observation that points at the old row via `ObservationPreviousVersion`.
 */
export async function fetchLaborStageObservationsWithMembersForVersionDiff(
  medplum: MedplumClient,
  clientId: string,
  encounterId: string,
): Promise<Observation[]> {
  const baseObservations = await fetchLaborStageObservationsWithMembers(
    medplum,
    clientId,
    encounterId,
  );

  const includedObservationIds = new Set<string>();
  const includedQuestionnaireResponseIds = new Set<string>();

  for (const observation of baseObservations) {
    if (observation.code?.coding?.[0]?.code === LOINCCodes.LaborStage) {
      const seeds = collectLaborStageMemberSeedIdsFromReferences(observation.hasMember);
      for (const observationId of seeds.observationIds) {
        includedObservationIds.add(observationId);
      }
      for (const questionnaireResponseId of seeds.questionnaireResponseIds) {
        includedQuestionnaireResponseIds.add(questionnaireResponseId);
      }
      if (observation.id) {
        const partOfLinkedQuestionnaireResponseIds =
          await fetchQuestionnaireResponseIdsLinkedToLaborStage(
            medplum,
            observation,
            clientId,
            encounterId,
          );
        for (const questionnaireResponseId of partOfLinkedQuestionnaireResponseIds) {
          includedQuestionnaireResponseIds.add(questionnaireResponseId);
        }
      }
      continue;
    }
    if (observation.id) {
      includedObservationIds.add(observation.id);
    }
  }

  if (includedObservationIds.size === 0 && includedQuestionnaireResponseIds.size === 0) {
    return baseObservations;
  }

  const patientReference = joinReferenceString("Patient", clientId);
  const encounterReference = joinReferenceString("Encounter", encounterId);
  const encounterObservations = await medplum.searchResources("Observation", {
    subject: patientReference,
    encounter: encounterReference,
    _sort: "-_lastUpdated",
  });

  const observationsById = new Map<string, Observation>();
  for (const observation of [...baseObservations, ...encounterObservations]) {
    if (observation.id) {
      observationsById.set(observation.id, observation);
    }
  }

  expandIncludedLaborFlowsheetObservationIdsUntilStable(
    encounterObservations,
    includedObservationIds,
    includedQuestionnaireResponseIds,
    observationsById,
  );

  const includedIds = includedObservationIds;

  return [...observationsById.values()].filter((observation) => {
    if (!observation.id) {
      return false;
    }
    const isLaborStageParent = observation.code?.coding?.[0]?.code === LOINCCodes.LaborStage;
    return includedIds.has(observation.id) || isLaborStageParent;
  });
}

/**
 * Fetches postpartum observations for version diff
 */
export async function fetchPostpartumObservationsForDiff(
  medplum: MedplumClient,
  clientId: string,
  encounterId: string,
) {
  const patientReference = joinReferenceString("Patient", clientId);
  const encounterReference = joinReferenceString("Encounter", encounterId);

  const allObservations = await medplum.searchResources("Observation", {
    subject: patientReference,
    encounter: encounterReference,
    _sort: "-_lastUpdated",
  });

  const isPostpartumObservation = (obs: Observation) => {
    const code = obs.code?.coding?.[0]?.code;

    // Exclude labor stage observations
    if (code === LOINCCodes.LaborStage) {
      return false;
    }

    // Check if it's a client postpartum form
    const isClientForm = Object.values(ClientPostpartumFlowsheetForms).some(
      (formCode) => code?.includes(formCode) || code === formCode,
    );

    // Check if it's a newborn postpartum form
    const isNewbornForm = Object.values(NewbornPostpartumFlowsheetForms).some(
      (formCode) => code?.includes(formCode) || code === formCode,
    );

    // Check if it's a client questionnaire type
    const isClientQuestionnaire = Object.values(PostpartumClientQuestionnaireType).some(
      (questionnaireType) => code === questionnaireType,
    );

    // Check if it's a newborn questionnaire type
    const isNewbornQuestionnaire = Object.values(PostpartumNewbornQuestionnaireType).some(
      (questionnaireType) => code === questionnaireType,
    );

    // Check if it's an SOS questionnaire type
    const isSOSQuestionnaire = Object.values(PostpartumSOSQuestionnaireType).some(
      (questionnaireType) => code === questionnaireType,
    );

    return (
      isClientForm ||
      isNewbornForm ||
      isClientQuestionnaire ||
      isNewbornQuestionnaire ||
      isSOSQuestionnaire
    );
  };

  const postpartumObservations = allObservations.filter(isPostpartumObservation);

  return postpartumObservations.filter((observation) => Boolean(observation.id));
}

export function getCurrentLaborStage(observations: Observation[]): Observation | undefined {
  const orderedLaborStages = [
    LOINCAnswerCodes.SecondStageLabor,
    LOINCAnswerCodes.ActiveLabor,
    LOINCAnswerCodes.EarlyLabor,
    LOINCAnswerCodes.ProdromalLabor,
  ];

  // Filter out cancelled observations
  const activeObservations = observations.filter((obs) => obs.status !== "cancelled");

  const sortedComponents = activeObservations.sort((a, b) => {
    const aCode = a.valueCodeableConcept?.coding?.[0]?.code as LOINCAnswerCodes | undefined;
    const bCode = b.valueCodeableConcept?.coding?.[0]?.code as LOINCAnswerCodes | undefined;

    if (!aCode || !orderedLaborStages.includes(aCode)) return 1;
    if (!bCode || !orderedLaborStages.includes(bCode)) return -1;

    return orderedLaborStages.indexOf(aCode) - orderedLaborStages.indexOf(bCode);
  });

  return sortedComponents?.[0];
}

/** One in-flight `getBirthingObservations` per encounter+client while parallel `getLaborStageRows` run. */
const activeBirthingObservationsInflight = new Map<string, Promise<Observation[]>>();

function getActiveBirthingObservationsForEncounterCached(
  medplum: MedplumClient,
  encounterId: string,
  clientId: string,
): Promise<Observation[]> {
  const cacheKey = `${encounterId}:${clientId}`;
  let inflightPromise = activeBirthingObservationsInflight.get(cacheKey);
  if (!inflightPromise) {
    inflightPromise = getBirthingObservations(medplum, encounterId, clientId).finally(() => {
      activeBirthingObservationsInflight.delete(cacheKey);
    });
    activeBirthingObservationsInflight.set(cacheKey, inflightPromise);
  }
  return inflightPromise;
}

async function fetchRelatedObservations(
  medplum: MedplumClient,
  laborStageObservation: Observation | undefined,
) {
  if (!laborStageObservation) return [];

  const memberIds =
    laborStageObservation.hasMember
      ?.map((member) => parseReference(member)[1])
      .filter((id): id is string => Boolean(id)) ?? [];
  if (!memberIds || memberIds.length === 0) return [];

  const response = await medplum.searchResources(
    "Observation",
    {
      _id: memberIds.join(","),
      subject: laborStageObservation.subject?.reference,
      encounter: laborStageObservation.encounter?.reference,
      _sort: "-_lastUpdated",
      _count: memberIds.length,
    },
    MEDPLUM_CACHE_RELOAD_OPTIONS,
  );
  return getResourcesFromBundle("Observation", response.bundle);
}

/** True if Observation uses the birthing flowsheet code (any status). `isBirthingObservation` is false when cancelled. */
function observationHasBirthingFlowsheetCode(observation: Observation): boolean {
  return Boolean(
    observation.code?.coding?.some((coding) => coding.code === LaborFlowsheetForms.Birthing),
  );
}

/** Row grouping requires a datetime; birthing Observations may omit `effectiveDateTime` while components have times. */
function getEffectiveDateTimeForLaborRowGrouping(observation: Observation): string | undefined {
  if (observation.effectiveDateTime) {
    return observation.effectiveDateTime;
  }
  if (!observationHasBirthingFlowsheetCode(observation)) {
    return observation.issued ?? observation.meta?.lastUpdated;
  }
  const componentCodes = [
    BirthingComponent.BodyDeliveredDateTime,
    BirthingComponent.HeadDeliveredDateTime,
    BirthingComponent.CrownDeliveredDateTime,
  ];
  for (const code of componentCodes) {
    const value = observation.component?.find((c) =>
      c.code?.coding?.some((x) => x.code === code),
    )?.valueDateTime;
    if (value) return value;
  }
  return observation.issued ?? observation.meta?.lastUpdated;
}

/**
 * If this labor stage's `hasMember` still points at a **cancelled** birthing Observation (e.g.
 * after a bot supersede) but no **final** birthing is in the member set, append the active birthing
 * row from an encounter search — only for **this** stage, preserving "created under whichever stage
 * was active" without moving rows to a different stage.
 */
async function mergeActiveBirthingWhenMembersHadCancelledBirthing(
  medplum: MedplumClient,
  laborStageObservation: Observation,
  allFetchedMembers: Observation[],
  finalMembers: Observation[],
): Promise<Observation[]> {
  const hadCancelledBirthing = allFetchedMembers.some(
    (memberObservation) =>
      memberObservation.status === "cancelled" &&
      observationHasBirthingFlowsheetCode(memberObservation),
  );
  const hasFinalBirthing = finalMembers.some((memberObservation) =>
    isBirthingObservation(memberObservation),
  );
  if (!hadCancelledBirthing || hasFinalBirthing) {
    return finalMembers;
  }

  const clientId = resolveId(laborStageObservation.subject);
  const encounterId = resolveId(laborStageObservation.encounter);
  if (!clientId || !encounterId) {
    return finalMembers;
  }

  try {
    const activeBirthingObservations = await getActiveBirthingObservationsForEncounterCached(
      medplum,
      encounterId,
      clientId,
    );
    if (activeBirthingObservations.length === 0) {
      return finalMembers;
    }

    const finalMemberObservationsById = new Map(
      finalMembers.map((finalMemberObservation) => [
        finalMemberObservation.id,
        finalMemberObservation,
      ]),
    );
    const mergedFinalAndActiveBirthing = [...finalMembers];
    for (const activeBirthingObservation of activeBirthingObservations) {
      if (
        activeBirthingObservation.id &&
        !finalMemberObservationsById.has(activeBirthingObservation.id)
      ) {
        mergedFinalAndActiveBirthing.push(activeBirthingObservation);
        finalMemberObservationsById.set(activeBirthingObservation.id, activeBirthingObservation);
      }
    }
    return mergedFinalAndActiveBirthing;
  } catch (error) {
    Sentry.captureException(error, {
      tags: { area: "labor-flowsheet", merge: "active-birthing-after-cancelled-member" },
      extra: { encounterId, clientId },
    });
    return finalMembers;
  }
}

export async function getLaborStageRows(
  medplum: MedplumClient,
  laborStageObservation: Observation | undefined,
) {
  // Filter out missing or cancelled labor stage observations
  if (!laborStageObservation || laborStageObservation.status === "cancelled") {
    return {};
  }

  const allMemberObservations = await fetchRelatedObservations(medplum, laborStageObservation);
  const finalMemberObservations = allMemberObservations.filter(
    (memberObservation) => memberObservation.status === "final",
  );

  const relatedObservations = await mergeActiveBirthingWhenMembersHadCancelledBirthing(
    medplum,
    laborStageObservation,
    allMemberObservations,
    finalMemberObservations,
  );

  const rowsByDate = [laborStageObservation, ...relatedObservations].reduce<
    Record<string, StageRow[]>
  >((result, observation) => {
    if (!observation) return result;

    const effectiveDateTime = getEffectiveDateTimeForLaborRowGrouping(observation);
    if (!effectiveDateTime) return result;

    // Use local date so rows appear on the calendar day in the viewer's timezone (fixes 4am
    // and other times appearing on the wrong day when stored without timezone or in UTC).
    const date = getLocalDateString(effectiveDateTime) || effectiveDateTime.split("T")[0];
    const time = localTimeFromISO(effectiveDateTime);

    if (!result[date]) {
      result[date] = [];
    }

    let timeRow = result[date].find((row) => row.time === time);
    if (!timeRow) {
      timeRow = { time, observations: [] };
      result[date].push(timeRow);
    }

    timeRow.observations.push(observation);
    return result;
  }, {});

  // Sort dates in descending order and times within each date
  const sortedDates = Object.keys(rowsByDate).sort((a, b) => b.localeCompare(a));

  sortedDates.forEach((date) => {
    rowsByDate[date].sort((a, b) => b.time.localeCompare(a.time));
  });
  // `pick` ensures that the dates are in descending order
  return pick(rowsByDate, sortedDates);
}

export async function completeAllEncounterTasks(
  medplum: MedplumClient,
  encounterId: string,
  clientId: string,
) {
  try {
    // Search for all Tasks bound to this encounter
    const tasks = await medplum.searchResources("Task", {
      encounter: `Encounter/${encounterId}`,
      subject: `Patient/${clientId}`,
      intent: "order",
      status: "in-progress,requested,accepted",
    });

    // Update all tasks to completed status
    const updatePromises = tasks.map(async (task) => {
      const updatedTask = {
        ...task,
        status: "completed" as const,
        executionPeriod: {
          ...task.executionPeriod,
          end: new Date().toISOString(),
        },
      };
      return medplum.updateResource(updatedTask);
    });

    await Promise.all(updatePromises);
  } catch (error) {
    console.error("Error completing encounter tasks:", error);
    // Don't throw the error to avoid breaking the main flow
    // The task completion is a side effect and shouldn't prevent the birthing submission
  }
}

export async function saveEncounterParticipants(
  medplum: MedplumClient,
  encounter: Encounter,
  values: BeforeChartingFormValues,
  options?: { preserveExistingOthersPresent?: boolean },
) {
  const profileReference = createReference(medplum.getProfile() as Practitioner);
  const shouldPreserveExistingOthersPresent = options?.preserveExistingOthersPresent === true;

  const getParticipant = (
    reference: Reference<Practitioner>,
    type?: EncounterPractitionerType,
  ) => ({
    individual: reference,
    type: type
      ? [
          {
            coding: [
              {
                code: type,
                display: EncounterPractitionerTypeDisplay[type],
                system: QuiltedHealthCodeSystems.EncounterPractitionerType,
              },
            ],
          },
        ]
      : undefined,
  });

  const getOthersPresentParticipant = (person: {
    name?: string;
    relationshipToClient?: string;
    reference?: string;
  }): EncounterParticipant => {
    const name = person.name?.trim();
    const relationshipToClient = person.relationshipToClient?.trim();
    if (!name || !relationshipToClient) {
      throw new Error(
        "Others Present requires a name and relationship to client for each selected person",
      );
    }

    const participant: EncounterParticipant = {
      individual: person.reference
        ? { reference: person.reference, display: name }
        : { display: name },
      type: [
        {
          coding: [
            {
              code: EncounterPractitionerType.OthersPresent,
              display: EncounterPractitionerTypeDisplay[EncounterPractitionerType.OthersPresent],
              system: QuiltedHealthCodeSystems.EncounterPractitionerType,
            },
          ],
        },
      ],
    };

    // Preload uses "Unknown" for legacy rows missing the extension; omit writing it back
    // so those rows stay extension-free until the user sets a real relationship.
    if (relationshipToClient !== OTHERS_PRESENT_UNKNOWN_RELATIONSHIP) {
      participant.extension = setExtensionValue(
        participant.extension,
        Extensions.EncounterParticipantRelationshipToClient,
        { valueString: relationshipToClient },
      );
    }

    return participant;
  };

  const participant: EncounterParticipant[] = [
    getParticipant(profileReference, EncounterPractitionerType.Participant),
  ];

  if (values.provider?.length) {
    values.provider.forEach((provider) => {
      participant.push(getParticipant(provider, EncounterPractitionerType.Provider));
    });
  }

  if (values.hasBirthingAssistant && values.birthingAssistant?.length) {
    values.birthingAssistant.forEach((assistant) => {
      participant.push(getParticipant(assistant, EncounterPractitionerType.BirthingAssistant));
    });
  }

  if (shouldPreserveExistingOthersPresent) {
    const existingOthersPresent =
      encounter.participant?.filter((existingParticipant) =>
        isDoulaOrOthersPresent(existingParticipant),
      ) ?? [];
    participant.push(...existingOthersPresent);
  } else if (values.hasOthersPresent && values.othersPresent?.length) {
    values.othersPresent.forEach((other) => {
      participant.push(getOthersPresentParticipant(other));
    });
  }

  const currentEncounterVersionId = encounter.meta?.versionId;
  return await medplum.updateResource(
    { ...encounter, participant },
    currentEncounterVersionId ? { headers: { "If-Match": currentEncounterVersionId } } : {},
  );
}

export const getQuestionnaire = async (
  medplum: MedplumClient,
  questionnaireName: string,
  bypassCache = false,
) => {
  try {
    let result;

    if (bypassCache) {
      // Force fresh fetch by using a different approach - clear cache and fetch
      try {
        // Try to clear any cached data by using a unique search parameter
        const uniqueParam = `_cache_bust_${Date.now()}`;
        result = await medplum.searchResources("Questionnaire", {
          name: questionnaireName,
          _sort: uniqueParam,
        });
      } catch (cacheError) {
        result = await medplum.searchResources("Questionnaire", { name: questionnaireName });
      }
    } else {
      result = await medplum.searchResources("Questionnaire", { name: questionnaireName });
    }

    // If not found and the name starts with forms/laborFlowSheet/, try with just the last part
    // (for labor forms displayed in postpartum flowsheets)
    if (!result.length && questionnaireName.startsWith("forms/laborFlowSheet/")) {
      const name = questionnaireName.split("/").pop();
      if (name) {
        if (bypassCache) {
          try {
            const uniqueParam = `_cache_bust_${Date.now()}`;
            result = await medplum.searchResources("Questionnaire", {
              name,
              _sort: uniqueParam,
            });
          } catch (cacheError) {
            result = await medplum.searchResources("Questionnaire", { name });
          }
        } else {
          result = await medplum.searchResources("Questionnaire", { name });
        }
      }
    }

    return result[0];
  } catch (err) {
    showSystemErrorNotification();
    throw err;
  }
};

/**
 * Adds versioning extensions to a questionnaire response, maintaining the history of previous versions.
 */
export function addQuestionnaireResponseVersioning(
  questionnaireResponse: QuestionnaireResponse,
  existingQuestionnaireResponse?: QuestionnaireResponse,
  observation?: Observation,
  authorReference?: string,
): QuestionnaireResponse {
  if (!observation) {
    return questionnaireResponse;
  }

  // Get all previous versions from the existing questionnaire response
  const previousVersions =
    existingQuestionnaireResponse?.extension?.filter(
      (ext) => ext.url === Extensions.ObservationPreviousVersion,
    ) || [];

  // Calculate the new version number based on previous versions
  const versionNumber = previousVersions.length + 1;

  // Filter out any existing version extension
  const otherExtensions = (questionnaireResponse.extension || []).filter(
    (ext) => ext.url !== Extensions.QuestionnaireResponseNamedVersion,
  );

  // Build the new extensions array
  const newExtensions = [
    ...otherExtensions,
    {
      url: Extensions.QuestionnaireResponseNamedVersion,
      valueString: `v${versionNumber}`,
    },
    ...previousVersions,
    {
      url: Extensions.ObservationPreviousVersion,
      valueReference: {
        reference: joinReferenceString("Observation", observation.id!),
      },
    },
  ];

  // Add the author extension if provided
  if (authorReference) {
    newExtensions.push({
      url: Extensions.EditedBy,
      valueReference: {
        reference: authorReference,
      },
    });
  }

  return {
    ...questionnaireResponse,
    extension: newExtensions,
  };
}

export const createLaborFlowsheetQuestionnaireResponse = async (
  medplum: MedplumClient,
  clientId: string,
  encounterId: string,
  practitioner: Practitioner,
  questionnaireName: string,
  values: QuestionnaireFormValues,
  currentLaborStageReference: Reference<Observation> | undefined,
  onRefresh?: () => Promise<void>,
  observation?: Observation,
  encounterQuestionnaireParentVersionId?: string,
  // Overrides `clientId` as this QR's subject -- used for a "newborn"-domain flowsheet entry once
  // the specific newborn it belongs to has been resolved (see useNewbornFlowsheetSubject), so the
  // entry attributes to the newborn's own Patient instead of the mother's.
  subjectPatientId?: string,
) => {
  try {
    const questionnaire = await getQuestionnaire(medplum, questionnaireName);
    if (!questionnaire) {
      throw new Error(`Questionnaire ${questionnaireName} not found`);
    }

    const encounterReference = joinReferenceString("Encounter", encounterId);
    const patientReference = joinReferenceString("Patient", subjectPatientId ?? clientId);

    const profile = medplum.getProfile() as Practitioner;
    const author = practitioner ?? profile;

    // DateTime groups should carry UTC-consistent valueDate + valueTime (see localDateAndTimeToUTCDateAndTime
    // on submit). Bots build effectiveDateTime as `${date}T${time}Z`.
    const questionnaireResponse = getQuestionnaireResponseFromFormValues(
      questionnaire,
      values,
      { reference: patientReference },
      createReference(author),
    );

    questionnaireResponse.encounter = { reference: encounterReference };
    questionnaireResponse.status = "completed";

    // Add versioning extensions if we have an observation to replace
    const authorReference = author.id ? joinReferenceString("Practitioner", author.id) : undefined;
    const versionedResponse = observation
      ? addQuestionnaireResponseVersioning(
          questionnaireResponse,
          await medplum.readResource(
            "QuestionnaireResponse",
            getQuestionnaireResponseIdFromObservationDerivedFrom(observation.derivedFrom) ?? "",
          ),
          observation,
          authorReference,
        )
      : questionnaireResponse;

    // Check if the questionnaire is a postpartum questionnaire using the enums
    const isPostpartumQuestionnaire =
      Object.values(PostpartumClientQuestionnaireType).includes(
        questionnaireName as PostpartumClientQuestionnaireType,
      ) ||
      Object.values(PostpartumNewbornQuestionnaireType).includes(
        questionnaireName as PostpartumNewbornQuestionnaireType,
      );

    const isSOSQuestionnaire = Object.values(PostpartumSOSQuestionnaireType).includes(
      questionnaireName as PostpartumSOSQuestionnaireType,
    );

    if (
      (!isPostpartumQuestionnaire && currentLaborStageReference) ||
      (isSOSQuestionnaire && currentLaborStageReference)
    ) {
      versionedResponse.partOf = [currentLaborStageReference];
    }

    const stampedResponse = applyQuestionnaireResponseEncounterParentVersionId(
      versionedResponse,
      encounterQuestionnaireParentVersionId,
    );

    const response = await medplum.createResource(stampedResponse);
    await setAccountsBestEffort(medplum, response, [], { extractFromPatient: true });

    const isBirthingQuestionnaire = questionnaireName === "birthing";
    // Always refresh if no labor stage is provided (to pick up newly created Prodromal labor stage)
    // or if it's a postpartum/birthing questionnaire
    if (
      (!currentLaborStageReference || isPostpartumQuestionnaire || isBirthingQuestionnaire) &&
      onRefresh
    ) {
      await onRefresh();
    }

    return response;
  } catch (err) {
    showSystemErrorNotification();
    throw err;
  }
};

export const getLaborFlowsheetQuestionnaireResponse = async (
  medplum: MedplumClient,
  encounterId: string,
  questionnaireId: string,
): Promise<QuestionnaireResponse | undefined> => {
  try {
    const { bundle: questionnaireResponseBundle } = await medplum.searchResources(
      "QuestionnaireResponse",
      {
        encounter: joinReferenceString("Encounter", encounterId),
        questionnaire: `Questionnaire/${questionnaireId}`,
        _sort: "-authored",
        _count: 1,
      },
    );

    const response = getResourcesFromBundle(
      "QuestionnaireResponse",
      questionnaireResponseBundle,
    )[0];

    return response;
  } catch (err) {
    showSystemErrorNotification();
    throw err;
  }
};

/**
 * Helper function to either retrieve or create the target labor stage
 */
export async function getOrCreateLaborStage(
  medplum: MedplumClient,
  laborStageObservations: Observation[],
  stageKey: string,
  clientId: string | undefined,
  encounterId: string | undefined,
): Promise<Observation> {
  if (!clientId || !encounterId) {
    throw new Error("Missing clientId or encounterId");
  }

  let targetStage = laborStageObservations.find(
    (obs) => obs.valueCodeableConcept?.coding?.[0]?.code === stageKey,
  );

  if (!targetStage) {
    const newLaborStage: Observation = {
      resourceType: "Observation",
      status: "final",
      code: {
        coding: [
          {
            system: Systems.LOINC,
            code: LOINCCodes.LaborStage,
            display: "Labor stage [Type]",
          },
        ],
      },
      subject: { reference: `Patient/${clientId}` },
      encounter: { reference: `Encounter/${encounterId}` },
      effectiveDateTime: new Date().toISOString(),
      issued: new Date().toISOString(),
      valueCodeableConcept: {
        coding: [
          {
            system: Systems.LOINC,
            code: stageKey,
            display: LaborStages[stageKey as keyof typeof LaborStages],
          },
        ],
      },
    };
    targetStage = await medplum.createResource(newLaborStage);
  }

  return targetStage;
}

/**
 * Helper function to update observation membership in labor stages
 */
export async function updateObservationMembership(
  medplum: MedplumClient,
  currentObservationId: string | undefined,
  currentLaborStage: Observation | undefined,
  targetStage: Observation,
) {
  assertLaborAmendMembershipWriteAllowed();

  if (!currentObservationId) {
    throw new Error("Missing currentObservationId");
  }

  const observationRef: Reference<Observation> = {
    reference: `Observation/${currentObservationId}`,
  };

  // Find the associated birthDateTime observation if it exists, first by source obs, then by current stage
  const birthDateTimeObservation = await findAssociatedBirthDateTimeObservation(
    medplum,
    currentObservationId,
    currentLaborStage?.id,
  );

  const birthDateTimeRef = birthDateTimeObservation
    ? { reference: `Observation/${birthDateTimeObservation.id}` }
    : null;

  // Remove from current labor stage if it exists
  if (currentLaborStage) {
    // Fetch the most current version of the labor stage to avoid race conditions
    const currentLaborStageRefreshed = await medplum.readResource(
      "Observation",
      currentLaborStage.id!,
      MEDPLUM_CACHE_RELOAD_OPTIONS,
    );

    const updatedMembers =
      currentLaborStageRefreshed.hasMember?.filter(
        (member) =>
          member.reference !== observationRef.reference &&
          (!birthDateTimeObservation ||
            member.reference !== `Observation/${birthDateTimeObservation.id}`),
      ) || [];

    await medplum.updateResource({
      ...currentLaborStageRefreshed,
      hasMember: updatedMembers,
    });
  }

  // If we have a birth time, proactively remove it from ALL labor stages in this encounter
  if (birthDateTimeObservation) {
    try {
      const subjectRef = targetStage.subject?.reference;
      const encounterRef = targetStage.encounter?.reference;
      if (subjectRef && encounterRef) {
        const laborStages = await medplum.searchResources("Observation", {
          subject: subjectRef,
          encounter: encounterRef,
          code: joinTokenString(Systems.LOINC, LOINCCodes.LaborStage),
          "status:not": "cancelled",
          _sort: "-_lastUpdated",
        });

        for (const stage of laborStages) {
          const containsBirthTime = stage.hasMember?.some(
            (m) => m.reference === `Observation/${birthDateTimeObservation.id}`,
          );
          if (containsBirthTime) {
            const refreshed = await medplum.readResource("Observation", stage.id!, {
              ...MEDPLUM_CACHE_RELOAD_OPTIONS,
            });
            const updatedMembers = (refreshed.hasMember || []).filter(
              (m) => m.reference !== `Observation/${birthDateTimeObservation.id}`,
            );
            await medplum.updateResource({
              ...refreshed,
              hasMember: updatedMembers,
            });
          }
        }
      }
    } catch (e) {
      console.error("Error removing birth time from other labor stages:", e);
    }
  }

  // Proactively remove the parent observation from ALL labor stages in this encounter
  try {
    const subjectRef = targetStage.subject?.reference;
    const encounterRef = targetStage.encounter?.reference;
    if (subjectRef && encounterRef) {
      const laborStages = await medplum.searchResources("Observation", {
        subject: subjectRef,
        encounter: encounterRef,
        code: joinTokenString(Systems.LOINC, LOINCCodes.LaborStage),
        "status:not": "cancelled",
        _sort: "-_lastUpdated",
      });

      for (const stage of laborStages) {
        const containsParent = stage.hasMember?.some(
          (m) => m.reference === observationRef.reference,
        );
        if (containsParent) {
          const refreshed = await medplum.readResource("Observation", stage.id!, {
            ...MEDPLUM_CACHE_RELOAD_OPTIONS,
          });
          const updatedMembers = (refreshed.hasMember || []).filter(
            (m) => m.reference !== observationRef.reference,
          );
          await medplum.updateResource({
            ...refreshed,
            hasMember: updatedMembers,
          });
        }
      }
    }
  } catch (e) {
    console.error("Error removing parent observation from other labor stages:", e);
  }

  // Fetch the most current version of the target stage to avoid race conditions
  const targetStageRefreshed = await medplum.readResource("Observation", targetStage.id!, {
    ...MEDPLUM_CACHE_RELOAD_OPTIONS,
  });

  // Add to target labor stage (both main observation and birthDateTime if it exists)
  const membersToAdd = [observationRef];
  if (birthDateTimeRef) {
    membersToAdd.push(birthDateTimeRef);
  }

  const updatedTargetMembers = [...(targetStageRefreshed.hasMember || []), ...membersToAdd];

  await medplum.updateResource({
    ...targetStageRefreshed,
    hasMember: updatedTargetMembers,
  });
}

/**
 * Helper function to find the associated birthDateTime observation for a birthing or shoulder dystocia observation
 */
async function findAssociatedBirthDateTimeObservation(
  medplum: MedplumClient,
  sourceObservationId: string,
  currentLaborStageId?: string,
): Promise<Observation | undefined> {
  try {
    // Search for birthDateTime observations that have this observation as a source
    const { bundle } = await medplum.searchResources("Observation", {
      code: `${Systems.QH}|${QuiltedHealthCodes.BirthDateTime}`,
      status: "final",
    });

    const birthDateTimeObservations =
      bundle?.entry?.map((entry) => entry.resource as Observation) || [];

    // Find the birthDateTime observation that references this source observation
    let associatedBirthDateTime = birthDateTimeObservations.find((obs) => {
      const sourceExtension = obs.extension?.find((ext) => ext.url === Extensions.QhBirthSource);
      return sourceExtension?.valueReference?.reference === `Observation/${sourceObservationId}`;
    });

    // If not found by source, attempt to match by the current labor stage linkage extension
    if (!associatedBirthDateTime && currentLaborStageId) {
      associatedBirthDateTime = birthDateTimeObservations.find((obs) => {
        const stageExtension = obs.extension?.find(
          (ext) => ext.url === Extensions.QhBirthSourceLaborStage,
        );
        return stageExtension?.valueReference?.reference === `Observation/${currentLaborStageId}`;
      });
    }

    return associatedBirthDateTime;
  } catch (error) {
    console.error("Error finding associated birthDateTime observation:", error);
    return undefined;
  }
}

/**
 * Gets a questionnaire response from an observation's hasMember property
 * @param medplum Medplum client
 * @param observation The observation containing the questionnaire response reference
 * @returns The questionnaire response or undefined if not found
 */
export async function getQuestionnaireResponseFromObservation(
  medplum: MedplumClient,
  observation?: Observation,
): Promise<QuestionnaireResponse | undefined> {
  const questionnaireResponseId = getQuestionnaireResponseIdFromObservationDerivedFrom(
    observation?.derivedFrom,
  );
  if (!questionnaireResponseId) return undefined;

  try {
    return await medplum.readResource("QuestionnaireResponse", questionnaireResponseId);
  } catch (err) {
    console.error("Error fetching questionnaire response:", err);
    return undefined;
  }
}

/**
 * Finds a labor stage observation that contains the given observation as a member
 * @param medplum Medplum client
 * @param observation The observation to find the labor stage for
 * @param clientId The patient ID
 * @param encounterId The encounter ID
 * @returns A reference to the labor stage observation or undefined if not found
 */
export async function getLaborStageFromMember(
  medplum: MedplumClient,
  observation: Observation | undefined,
  clientId: string | undefined,
  encounterId: string | undefined,
): Promise<Reference<Observation> | undefined> {
  if (!observation || !clientId || !encounterId) {
    return undefined;
  }

  const patientReference = joinReferenceString("Patient", clientId);
  const encounterReference = joinReferenceString("Encounter", encounterId);

  const laborStageObservations = await medplum.searchResources("Observation", {
    subject: patientReference,
    encounter: encounterReference,
    code: joinTokenString(Systems.LOINC, LOINCCodes.LaborStage),
    "status:not": "cancelled",
  });

  // Find the labor stage that contains this observation as a member
  const parentLaborStage = laborStageObservations.find(
    (stage) =>
      Array.isArray(stage.hasMember) &&
      stage.hasMember.some((member) => member.reference === `Observation/${observation.id}`),
  );

  if (!parentLaborStage) {
    return undefined;
  }

  return { reference: `Observation/${parentLaborStage.id}` };
}

export async function getBirthingObservations(
  medplum: MedplumClient,
  encounterId: string,
  clientId: string,
) {
  const encounterReference = joinReferenceString("Encounter", encounterId);
  try {
    const observations = await medplum.searchResources("Observation", {
      encounter: encounterReference,
      code: `${Systems.QH}|${LaborFlowsheetForms.Birthing}`,
      subject: `Patient/${clientId}`,
      "status:not": "cancelled",
      _sort: "date",
    });
    return getResourcesFromBundle("Observation", observations.bundle);
  } catch (error) {
    // Format error message to match test expectations
    const errorMessage = error instanceof Error ? error.message : "Unknown error occurred";
    const formattedError = `Failed to retrieve birthing observations for encounter ${encounterId} and client ${clientId}: ${errorMessage}`;

    // Log the error in a more concise format
    console.error(formattedError);

    throw new Error(formattedError);
  }
}

export function getBodyDeliveryDate(obs: Observation): Date | null {
  const component = obs.component?.find((c) =>
    c.code?.coding?.some((coding) => coding.code === "body-delivery-date-time"),
  );
  if (!component?.valueDateTime) return null;

  const date = new Date(component.valueDateTime);
  return isNaN(date.getTime()) ? null : date;
}

export function getBodyBirthObservation(birthObservations: Observation[]) {
  // Filter for valid observations that have a body-delivery-date-time component
  const withBody = birthObservations.filter(
    (obs) => obs.status !== "cancelled" && Boolean(getBodyDeliveryDate(obs)),
  );

  // Sort by the extracted date
  withBody.sort((a, b) => {
    const aTime = getBodyDeliveryDate(a)?.getTime() || 0;
    const bTime = getBodyDeliveryDate(b)?.getTime() || 0;
    return aTime - bTime;
  });

  return withBody[0];
}

const MAX_PREVIOUS_VERSION_DEPTH = 3; // Reduced from 10 to prevent excessive recursion

// Cache to prevent multiple simultaneous calls for the same observation
const previousVersionCache = new Map<string, Promise<Observation[]>>();

// Global visited set to prevent processing the same observation multiple times across different calls
const globalVisitedIds = new Set<string>();

// Clear global visited set every 5 minutes to prevent memory leaks
setInterval(
  () => {
    globalVisitedIds.clear();
  },
  5 * 60 * 1000,
);

// Circuit breaker to prevent cascade failures
let errorCount = 0;
const MAX_ERRORS = 5;
const ERROR_RESET_TIME = 30000; // 30 seconds
let lastErrorTime = 0;

export async function getPreviousVersionObservations(
  medplum: MedplumClient,
  observation: Observation,
  visitedIds: Set<string> = new Set(),
  depth: number = 0,
): Promise<Observation[]> {
  // Circuit breaker: if too many errors recently, return empty array
  const now = Date.now();
  if (now - lastErrorTime < ERROR_RESET_TIME && errorCount >= MAX_ERRORS) {
    console.warn(
      `Circuit breaker open: too many errors (${errorCount}) in the last ${ERROR_RESET_TIME}ms`,
    );
    return [];
  }

  // Reset error count if enough time has passed
  if (now - lastErrorTime >= ERROR_RESET_TIME) {
    errorCount = 0;
  }

  // Prevent cycles and excessive recursion
  if (depth > MAX_PREVIOUS_VERSION_DEPTH) {
    console.warn(
      `Maximum recursion depth (${MAX_PREVIOUS_VERSION_DEPTH}) reached for observation ${observation.id}`,
    );
    return [];
  }
  if (observation.id && visitedIds.has(observation.id)) {
    // Silently return empty array for circular references - this is expected behavior
    return [];
  }
  if (observation.id) {
    visitedIds.add(observation.id);
  }

  // Check cache for top-level calls (depth 0) to prevent duplicate requests
  if (depth === 0 && observation.id) {
    const cacheKey = observation.id;
    if (previousVersionCache.has(cacheKey)) {
      return previousVersionCache.get(cacheKey)!;
    }

    // Also check global visited set to prevent processing the same observation multiple times
    if (globalVisitedIds.has(observation.id)) {
      return [];
    }
    globalVisitedIds.add(observation.id);
  }

  // Create the main function that does the work
  const fetchPreviousVersions = async (): Promise<Observation[]> => {
    // Get all previous version references from the observation's extensions
    const previousVersionRefs =
      observation.extension?.filter((ext) => ext.url === Extensions.ObservationPreviousVersion) ||
      [];

    if (previousVersionRefs.length === 0) {
      return [];
    }

    // Validate references before creating bundle
    const validRefs = previousVersionRefs.filter((ref) => {
      const reference = ref.valueReference?.reference;
      if (!reference) {
        console.warn("Invalid reference found in previous version extension:", ref);
        return false;
      }
      // Check if reference is properly formatted
      if (!reference.startsWith("Observation/")) {
        console.warn("Invalid reference format:", reference);
        return false;
      }
      return true;
    });

    if (validRefs.length === 0) {
      console.warn("No valid previous version references found for observation:", observation.id);
      return [];
    }

    // Create a bundle request to fetch all previous versions
    const bundle: Bundle = {
      resourceType: "Bundle",
      type: "batch",
      entry: validRefs.map((ref) => ({
        request: {
          method: "GET",
          url: ref.valueReference!.reference!,
        },
      })),
    };

    try {
      // Add timeout to prevent hanging requests
      const timeoutPromise = new Promise<never>((_, reject) => {
        setTimeout(() => reject(new Error("Request timeout")), 10000); // 10 second timeout
      });

      const response = await Promise.race([medplum.executeBatch(bundle), timeoutPromise]);

      // Handle batch response errors
      if (!response.entry) {
        console.warn("No entries in batch response for observation:", observation.id);
        return [];
      }

      // Filter out failed entries and extract successful observations
      const previousVersions = response.entry
        .filter((entry) => {
          if (entry.response?.status && !entry.response.status.startsWith("2")) {
            console.warn(
              `Failed to fetch observation: ${entry.response.status} - ${entry.request?.url}`,
            );
            return false;
          }
          return entry.resource?.resourceType === "Observation";
        })
        .map((entry) => entry.resource as Observation);

      if (previousVersions.length === 0) {
        return [];
      }

      // Get the version number from the extension
      const getVersionNumber = (obs: Observation): number => {
        const versionExt = obs.extension?.find(
          (ext) => ext.url === Extensions.QuestionnaireResponseNamedVersion,
        );
        if (!versionExt?.valueString) return 0;
        const versionStr = versionExt.valueString;
        return parseInt(versionStr.replace("v", ""), 10) || 0;
      };

      // Sort versions by version number in descending order
      previousVersions.sort((a, b) => getVersionNumber(b) - getVersionNumber(a));

      // Limit recursive calls to prevent excessive network requests
      const maxRecursiveCalls = Math.min(previousVersions.length, 3);
      const selectedVersions = previousVersions.slice(0, maxRecursiveCalls);

      // Recursively get previous versions of these observations, passing the same visitedIds set
      const olderVersions = await Promise.all(
        selectedVersions.map((obs) =>
          getPreviousVersionObservations(medplum, obs, visitedIds, depth + 1),
        ),
      );

      // Combine and deduplicate versions
      const allVersions = [...previousVersions, ...olderVersions.flat()];
      const uniqueVersions = allVersions.filter(
        (obs, index, self) => index === self.findIndex((o) => o.id === obs.id),
      );

      // Sort by version number
      return uniqueVersions.sort((a, b) => getVersionNumber(b) - getVersionNumber(a));
    } catch (error) {
      console.error("Error fetching previous version observations:", error);
      // Increment error count and update last error time for circuit breaker
      errorCount++;
      lastErrorTime = Date.now();
      // Don't propagate the error to prevent cascade failures
      return [];
    }
  };

  // For top-level calls, cache the promise to prevent duplicate requests
  if (depth === 0 && observation.id) {
    const cacheKey = observation.id;
    const cachedPromise = fetchPreviousVersions();
    previousVersionCache.set(cacheKey, cachedPromise);

    // Clean up cache after 5 minutes to prevent memory leaks
    setTimeout(
      () => {
        previousVersionCache.delete(cacheKey);
      },
      5 * 60 * 1000,
    );

    return cachedPromise;
  }

  // For recursive calls, don't cache
  return fetchPreviousVersions();
}

export const getEditedByReference = (observation: Observation) => {
  const editedByExtension = observation.extension?.find((ext) => ext.url === Extensions.EditedBy);
  return editedByExtension?.valueReference?.reference;
};

export const hasPreviousVersions = (observation: Observation) =>
  Array.isArray(observation.extension) &&
  observation.extension.some((ext) => ext.url === Extensions.ObservationPreviousVersion);

// Function to clear caches (useful for testing or manual cleanup)
export const clearPreviousVersionCaches = () => {
  previousVersionCache.clear();
  globalVisitedIds.clear();
  errorCount = 0;
  lastErrorTime = 0;
};
